import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    TextField,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Header from "../../components/Header";
import FloatingDocumentEditor from "../../components/FloatingWindows/floatingDocumentEditor";

// ==========================================
// ENDPOINTS
// ==========================================
const API_SCAN_INVOICE = "https://invoice.swancreate.com/scan_invoice/";
const API_LL_INVOICE = "https://api.swancreate.com/api/llm/AZ_Invoice";
const API_LL_INVOICE_ITEMS = "https://api.swancreate.com/api/llm/AZ_InvoiceItems";

// ==========================================
// STYLED COMPONENTS
// ==========================================
const Thumbnail = styled("img")({
    width: 80,
    height: 80,
    objectFit: "cover",
    cursor: "pointer",
    marginBottom: 8,
    borderRadius: 4,
    boxShadow: "0 0 5px rgba(76, 175, 80, 0.4)",
});

const DropZone = styled(Box)(({ theme }) => ({
    border: "2px dashed #4CAF50",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center",
    color: "#4CAF50",
    cursor: "pointer",
    marginBottom: "16px",
}));

// ==========================================
// MAIN COMPONENT
// ==========================================
const FIGAgentInvoiceBulkReview = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [invoiceData, setInvoiceData] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [progress, setProgress] = useState(0);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");
    const [selectedFileType, setSelectedFileType] = useState("");

    // ----------------------------------------
    // 1) SELECT FILES (via browse or drag & drop)
    // ----------------------------------------
    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length > 0) {
            setFiles((prev) => [...prev, ...selectedFiles]);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            setFiles((prev) => [...prev, ...droppedFiles]);
        }
    };

    // ----------------------------------------
    // 2) EXPORT INVOICES TO DB
    //    (continue even if some exports fail)
    // ----------------------------------------
    const exportInvoicesToDB = async (invoices) => {
        for (const inv of invoices) {
            // Skip invoices that failed processing
            if (inv.error) {
                console.warn(`Skipping export: ${inv.errorMessage}`);
                continue;
            }
            // Generate or reuse the invoice's apiid
            const invoiceApiid = inv.apiid ? inv.apiid : uuidv4();
            inv.apiid = invoiceApiid;

            // Basic fields
            const invoiceNumber =
                inv.InvoiceId?.value ||
                inv.InvoiceNumber?.value ||
                inv.InvoiceNumber ||
                "";
            const vendorName = inv.VendorName?.value || inv.VendorName || "";
            let vendorAddressStr = "";
            const addrObj = inv.VendorAddress?.value;
            if (addrObj && typeof addrObj === "object") {
                const { houseNumber, road, city, state, postalCode } = addrObj;
                vendorAddressStr = `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
            }
            const vendorAddressRecipient = inv.VendorAddressRecipient?.value || "";
            const customerName = inv.CustomerName?.value || inv.CustomerName || "";
            const customerId = inv.CustomerId?.value || inv.CustomerId || "";
            let customerAddressStr = "";
            const customerAddressObj = inv.CustomerAddress?.value;
            if (customerAddressObj && typeof customerAddressObj === "object") {
                const { houseNumber, road, city, state, postalCode } = customerAddressObj;
                customerAddressStr = `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
            }
            const invoiceDate = inv.InvoiceDate?.value || inv.InvoiceDate || null;
            const invoiceTotal = inv.InvoiceTotal?.value || inv.InvoiceTotal || 0;
            const amountDue = inv.AmountDue?.value || inv.AmountDue || 0;

            // Main invoice payload
            const headerPayload = {
                apiid: invoiceApiid,
                InvoiceNumber: invoiceNumber,
                VendorName: vendorName,
                VendorAddress: vendorAddressStr,
                VendorAddressRecipient: vendorAddressRecipient,
                CustomerName: customerName,
                CustomerId: customerId,
                CustomerAddress: customerAddressStr,
                InvoiceDate: invoiceDate,
                InvoiceTotal: invoiceTotal,
                AmountDue: amountDue,
                CreatedOn: new Date().toISOString(),
                isReviewed: false,
            };

            // Attempt to export main invoice
            try {
                await axios.post(API_LL_INVOICE, headerPayload);
            } catch (err) {
                console.error(`Failed to export invoice ${invoiceNumber}:`, err.message);
            }

            // If there are items, export each item
            if (inv.Items && Array.isArray(inv.Items)) {
                for (const item of inv.Items) {
                    const itemApiid = uuidv4();
                    const description = item.Description?.value || "";
                    const quantity = item.Quantity?.value || 0;
                    const unit =
                        item.Unit?.value !== null && item.Unit?.value !== undefined
                            ? String(item.Unit.value)
                            : "";
                    const unitPrice = item.UnitPrice?.value || 0;
                    const productCode = item.ProductCode?.value || "";
                    const itemDate = item.Date?.value || null;
                    const tax = item.Tax?.value || "";
                    const amount = item.Amount?.value || 0;

                    const itemPayload = {
                        apiid: itemApiid,
                        InvoiceApiId: invoiceApiid,
                        InvoiceNumber: invoiceNumber,
                        Description: description,
                        Quantity: quantity,
                        Unit: unit,
                        UnitPrice: unitPrice,
                        ProductCode: productCode,
                        ItemDate: itemDate,
                        Tax: tax,
                        Amount: amount,
                        CreatedOn: new Date().toISOString(),
                        isReviewed: false,
                    };

                    // Attempt to export item
                    try {
                        await axios.post(API_LL_INVOICE_ITEMS, itemPayload);
                    } catch (err) {
                        console.error(
                            `Failed to export item for invoice ${invoiceNumber}:`,
                            err.message
                        );
                    }
                }
            }
        }
    };

    // ----------------------------------------
    // 3) UPLOAD ALL FILES & SCAN
    // ----------------------------------------
    const handleUploadAll = async () => {
        if (!files.length) {
            setError("No files to upload.");
            return;
        }
        setError(null);
        setLoading(true);
        setProgress(0);

        let newChatEntries = [];
        const scannedInvoices = [];
        const totalFiles = files.length;

        // Process each file individually to ensure one failure doesn't break the batch
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            try {
                const formData = new FormData();
                formData.append("file", file, file.name);

                const response = await axios.post(API_SCAN_INVOICE, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                if (response.data.success) {
                    const newInvoices = response.data.invoice_data || [];
                    if (newInvoices.length === 0) {
                        // If no invoice data returned, mark as failed
                        scannedInvoices.push({
                            apiid: uuidv4(),
                            InvoiceNumber: "Not Processed",
                            VendorName: "Not Processed",
                            VendorAddress: "Not Processed",
                            CustomerName: "Not Processed",
                            CustomerAddress: "Not Processed",
                            InvoiceDate: "",
                            InvoiceTotal: 0,
                            AmountDue: 0,
                            error: true,
                            errorMessage: `No invoice data returned for file: ${file.name}`,
                        });
                        newChatEntries.push(`No invoice data returned for file: ${file.name}`);
                    } else {
                        // Attach scannedImage and file type for image/PDF viewing
                        newInvoices.forEach((invoice) => {
                            invoice.scannedImage = URL.createObjectURL(file);
                            invoice.scannedFileType = file.type;
                        });
                        scannedInvoices.push(...newInvoices);
                        newChatEntries = newChatEntries.concat(
                            newInvoices.map((invoice) => {
                                const vendor =
                                    invoice.VendorName?.value || invoice.VendorName || "N/A";
                                const invNum =
                                    invoice.InvoiceNumber?.value || invoice.InvoiceNumber || "N/A";
                                const total = new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                }).format(
                                    invoice.InvoiceTotal?.value || invoice.InvoiceTotal || 0
                                );
                                return `Processed invoice: ${vendor} - ${invNum} - Total: ${total}`;
                            })
                        );
                    }
                } else {
                    // If scanning failed for this file, add an error record
                    scannedInvoices.push({
                        apiid: uuidv4(),
                        InvoiceNumber: "Not Processed",
                        VendorName: "Not Processed",
                        VendorAddress: "Not Processed",
                        CustomerName: "Not Processed",
                        CustomerAddress: "Not Processed",
                        InvoiceDate: "",
                        InvoiceTotal: 0,
                        AmountDue: 0,
                        error: true,
                        errorMessage: `Scanning failed for file: ${file.name}`,
                    });
                    newChatEntries.push(`Scanning failed for file: ${file.name}`);
                }
            } catch (err) {
                // On exception, add an error record for the file
                scannedInvoices.push({
                    apiid: uuidv4(),
                    InvoiceNumber: "Not Processed",
                    VendorName: "Not Processed",
                    VendorAddress: "Not Processed",
                    CustomerName: "Not Processed",
                    CustomerAddress: "Not Processed",
                    InvoiceDate: "",
                    InvoiceTotal: 0,
                    AmountDue: 0,
                    error: true,
                    errorMessage: `Error processing file: ${file.name}`,
                });
                newChatEntries.push(`Error processing file: ${file.name}`);
                console.error(`Error processing file ${file.name}:`, err);
            }
            setProgress(i + 1);
        }

        // Update table with all scanned invoices (including errors)
        setInvoiceData(scannedInvoices);

        // Add system messages about scanned invoices
        setChatMessages((prev) => [
            ...prev,
            ...newChatEntries.map((text) => ({ sender: "system", text })),
        ]);

        // Export all successfully scanned invoices to DB
        exportInvoicesToDB(scannedInvoices);
        setLoading(false);
    };

    // ----------------------------------------
    // 4a) DOWNLOAD CSV - Detailed (Invoice Data with Items)
    // ----------------------------------------
    const handleDownloadInvoiceDetailed = () => {
        if (!invoiceData.length) {
            alert("No invoice data to download.");
            return;
        }

        // CSV header for detailed download
        const header = [
            "Invoice API ID",
            "Invoice Number",
            "Vendor Name",
            "Vendor Address",
            "Customer Name",
            "Customer Address",
            "Invoice Date",
            "Invoice Total",
            "Amount Due",
            "Item Description",
            "Item Quantity",
            "Item Unit",
            "Item Unit Price",
            "Item Product Code",
            "Item Date",
            "Item Tax",
            "Item Amount",
        ];

        let csvRows = [];
        csvRows.push(header.join(","));

        // For each invoice, potentially multiple rows (one per item)
        invoiceData.forEach((invoice) => {
            const invoiceApiid = invoice.apiid || "";
            const invoiceNumber =
                invoice.InvoiceId?.value ||
                invoice.InvoiceNumber?.value ||
                invoice.InvoiceNumber ||
                invoice.InvoiceId ||
                "";
            const vendorName =
                invoice.VendorName?.value || invoice.VendorName || "";
            let vendorAddress = "";
            if (
                invoice.VendorAddress?.value &&
                typeof invoice.VendorAddress.value === "object"
            ) {
                const { houseNumber, road, city, state, postalCode } =
                    invoice.VendorAddress.value;
                vendorAddress = `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
            } else {
                vendorAddress = invoice.VendorAddress || "";
            }

            const customerName =
                invoice.CustomerName?.value || invoice.CustomerName || "";
            let customerAddress = "";
            if (
                invoice.CustomerAddress?.value &&
                typeof invoice.CustomerAddress.value === "object"
            ) {
                const { houseNumber, road, city, state, postalCode } =
                    invoice.CustomerAddress.value;
                customerAddress = `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
            } else {
                customerAddress = invoice.CustomerAddress || "";
            }

            const invoiceDate =
                invoice.InvoiceDate?.value || invoice.InvoiceDate || "";
            const invoiceTotal =
                invoice.InvoiceTotal?.value || invoice.InvoiceTotal || 0;
            const amountDue =
                invoice.AmountDue?.value || invoice.AmountDue || 0;

            // If the invoice has items, generate multiple rows
            if (invoice.Items && Array.isArray(invoice.Items) && invoice.Items.length) {
                invoice.Items.forEach((item, idx) => {
                    const description = item.Description?.value || "";
                    const quantity = item.Quantity?.value || 0;
                    const unit = item.Unit?.value || "";
                    const unitPrice = item.UnitPrice?.value || 0;
                    const productCode = item.ProductCode?.value || "";
                    const itemDate = item.Date?.value || "";
                    const tax = item.Tax?.value || "";
                    const amount = item.Amount?.value || 0;

                    // For the first item, show invoice info; otherwise blank out invoice columns
                    if (idx === 0) {
                        csvRows.push([
                            `"${invoiceApiid}"`,
                            `"${invoiceNumber}"`,
                            `"${vendorName}"`,
                            `"${vendorAddress}"`,
                            `"${customerName}"`,
                            `"${customerAddress}"`,
                            `"${invoiceDate}"`,
                            `"${invoiceTotal}"`,
                            `"${amountDue}"`,
                            `"${description}"`,
                            quantity,
                            `"${unit}"`,
                            unitPrice,
                            `"${productCode}"`,
                            `"${itemDate}"`,
                            `"${tax}"`,
                            amount,
                        ].join(","));
                    } else {
                        csvRows.push([
                            "",
                            "",
                            "",
                            "",
                            "",
                            "",
                            "",
                            "",
                            "",
                            `"${description}"`,
                            quantity,
                            `"${unit}"`,
                            unitPrice,
                            `"${productCode}"`,
                            `"${itemDate}"`,
                            `"${tax}"`,
                            amount,
                        ].join(","));
                    }
                });
            } else {
                // No items: single row for the invoice
                csvRows.push([
                    `"${invoiceApiid}"`,
                    `"${invoiceNumber}"`,
                    `"${vendorName}"`,
                    `"${vendorAddress}"`,
                    `"${customerName}"`,
                    `"${customerAddress}"`,
                    `"${invoiceDate}"`,
                    `"${invoiceTotal}"`,
                    `"${amountDue}"`,
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                ].join(","));
            }
        });

        // Convert array of rows to CSV string
        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "invoices_detailed.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ----------------------------------------
    // 4b) DOWNLOAD CSV - Summary (Invoice Data only)
    // ----------------------------------------
    const handleDownloadInvoiceSummary = () => {
        if (!invoiceData.length) {
            alert("No invoice data to download.");
            return;
        }

        // CSV header for summary download (only main invoice information)
        const header = [
            "Invoice API ID",
            "Invoice Number",
            "Vendor Name",
            "Vendor Address",
            "Customer Name",
            "Customer Address",
            "Invoice Date",
            "Invoice Total",
            "Amount Due",
        ];

        let csvRows = [];
        csvRows.push(header.join(","));

        invoiceData.forEach((invoice) => {
            const invoiceApiid = invoice.apiid || "";
            const invoiceNumber =
                invoice.InvoiceId?.value ||
                invoice.InvoiceNumber?.value ||
                invoice.InvoiceNumber ||
                invoice.InvoiceId ||
                "";
            const vendorName =
                invoice.VendorName?.value || invoice.VendorName || "";
            let vendorAddress = "";
            if (
                invoice.VendorAddress?.value &&
                typeof invoice.VendorAddress.value === "object"
            ) {
                const { houseNumber, road, city, state, postalCode } =
                    invoice.VendorAddress.value;
                vendorAddress = `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
            } else {
                vendorAddress = invoice.VendorAddress || "";
            }

            const customerName =
                invoice.CustomerName?.value || invoice.CustomerName || "";
            let customerAddress = "";
            if (
                invoice.CustomerAddress?.value &&
                typeof invoice.CustomerAddress.value === "object"
            ) {
                const { houseNumber, road, city, state, postalCode } =
                    invoice.CustomerAddress.value;
                customerAddress = `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
            } else {
                customerAddress = invoice.CustomerAddress || "";
            }

            const invoiceDate =
                invoice.InvoiceDate?.value || invoice.InvoiceDate || "";
            const invoiceTotal =
                invoice.InvoiceTotal?.value || invoice.InvoiceTotal || 0;
            const amountDue =
                invoice.AmountDue?.value || invoice.AmountDue || 0;

            csvRows.push([
                `"${invoiceApiid}"`,
                `"${invoiceNumber}"`,
                `"${vendorName}"`,
                `"${vendorAddress}"`,
                `"${customerName}"`,
                `"${customerAddress}"`,
                `"${invoiceDate}"`,
                `"${invoiceTotal}"`,
                `"${amountDue}"`,
            ].join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "invoices_summary.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ----------------------------------------
    // 5) SEND CHAT (with invoice context)
    // ----------------------------------------
    const handleSendChat = async () => {
        if (chatInput.trim() === "") return;

        // Add user message to chat
        const userMessage = { sender: "user", text: chatInput };
        setChatMessages((prev) => [...prev, userMessage]);

        // Build invoice context for the system prompt (not visible to user)
        const invoiceContext = invoiceData
            .map((inv) => {
                const invNum =
                    inv.InvoiceId?.value ||
                    inv.InvoiceNumber?.value ||
                    inv.InvoiceNumber ||
                    inv.InvoiceId ||
                    "N/A";
                const vendor =
                    inv.VendorName?.value || inv.VendorName || "N/A";
                const total = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(inv.InvoiceTotal?.value || inv.InvoiceTotal || 0);
                let itemsContext = "";
                if (inv.Items && Array.isArray(inv.Items)) {
                    itemsContext = inv.Items
                        .map((item) => {
                            const desc = item.Description?.value || "";
                            const qty = item.Quantity?.value || 0;
                            const unit = item.Unit?.value || "";
                            const price = new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                            }).format(item.UnitPrice?.value || 0);
                            const code = item.ProductCode?.value || "";
                            const date = item.Date?.value || "";
                            const tax = item.Tax?.value || "";
                            const amt = new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                            }).format(item.Amount?.value || 0);
                            return `(${desc}, ${qty}, ${unit}, ${price}, ${code}, ${date}, ${tax}, ${amt})`;
                        })
                        .join("; ");
                }
                return `Invoice ${invNum} from ${vendor} totaling ${total}. Items: ${itemsContext}`;
            })
            .join("\n");

        const messages = [
            {
                role: "system",
                content: `You are an assistant helping with invoice queries. The following is the full invoice context (do not show this to the user):\n${invoiceContext}`,
            },
            { role: "user", content: chatInput },
        ];

        try {
            const response = await axios.post(
                "https://api.openai.com/v1/chat/completions",
                {
                    model: "gpt-3.5-turbo",
                    messages,
                },
                {
                    headers: {
                        Authorization: `Bearer sk-cI0ZWW9VJ8vK5aA0rBBqQZ-ynifynWgkm1e_W4VidDT3BlbkFJoVl4MqwPNmbpfpizrCpW7I7B0ezKqqAp3WNIghfIgA`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const aiResponse = response.data.choices[0].message.content;
            setChatMessages((prev) => [
                ...prev,
                { sender: "agent", text: aiResponse },
            ]);
        } catch (error) {
            console.error("Error calling OpenAI API:", error);
            setChatMessages((prev) => [
                ...prev,
                { sender: "agent", text: "Error retrieving response from AI." },
            ]);
        }
        setChatInput("");
    };

    // ----------------------------------------
    // Handle row update - update the row data in the database using the apiid
    // ----------------------------------------
    const handleProcessRowUpdate = async (newRow, oldRow) => {
        try {
            const { apiid, ...updatePayload } = newRow;
            await axios.put(`${API_LL_INVOICE}/${apiid}`, updatePayload);
            return newRow;
        } catch (error) {
            console.error("Error updating row", error);
            throw error;
        }
    };

    // ----------------------------------------
    // DATA GRID COLUMNS (with row editing enabled and view image tool tip)
    // ----------------------------------------
    const columns = [
        {
            field: "apiid",
            headerName: "API ID",
            flex: 1,
            valueGetter: (params) => params.row.apiid || "N/A",
        },
        {
            field: "VendorName",
            headerName: "Vendor Name",
            flex: 1,
            editable: true,
            valueGetter: (params) =>
                params.row.VendorName?.value || params.row.VendorName || "N/A",
            valueSetter: (params) => {
                params.row.VendorName = params.value;
                return { ...params.row };
            },
        },
        {
            field: "VendorAddress",
            headerName: "Vendor Address",
            flex: 1,
            editable: true,
            valueGetter: (params) => {
                if (params.row.VendorAddress?.value) {
                    const addrObj = params.row.VendorAddress.value;
                    if (typeof addrObj === "object") {
                        const { houseNumber, road, city, state, postalCode } = addrObj;
                        return `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
                    }
                    return params.row.VendorAddress.value;
                }
                return params.row.VendorAddress || "N/A";
            },
            valueSetter: (params) => {
                params.row.VendorAddress = params.value;
                return { ...params.row };
            },
        },
        {
            field: "CustomerName",
            headerName: "Customer Name",
            flex: 1,
            editable: true,
            valueGetter: (params) =>
                params.row.CustomerName?.value || params.row.CustomerName || "N/A",
            valueSetter: (params) => {
                params.row.CustomerName = params.value;
                return { ...params.row };
            },
        },
        {
            field: "CustomerAddress",
            headerName: "Customer Address",
            flex: 1,
            editable: true,
            valueGetter: (params) => {
                if (params.row.CustomerAddress?.value) {
                    const addrObj = params.row.CustomerAddress.value;
                    if (typeof addrObj === "object") {
                        const { houseNumber, road, city, state, postalCode } = addrObj;
                        return `${houseNumber || ""} ${road || ""}, ${city || ""} ${state || ""} ${postalCode || ""}`.trim();
                    }
                    return params.row.CustomerAddress.value;
                }
                return params.row.CustomerAddress || "N/A";
            },
            valueSetter: (params) => {
                params.row.CustomerAddress = params.value;
                return { ...params.row };
            },
        },
        {
            field: "InvoiceDate",
            headerName: "Invoice Date",
            flex: 1,
            editable: true,
            valueGetter: (params) => {
                const val = params.row.InvoiceDate?.value || params.row.InvoiceDate;
                if (!val) return "N/A";
                const dt = new Date(val);
                return dt.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                });
            },
            valueSetter: (params) => {
                params.row.InvoiceDate = params.value;
                return { ...params.row };
            },
        },
        {
            field: "InvoiceNumber",
            headerName: "Invoice Number",
            flex: 1,
            editable: true,
            valueGetter: (params) =>
                params.row.InvoiceId?.value ||
                params.row.InvoiceNumber?.value ||
                params.row.InvoiceNumber ||
                params.row.InvoiceId ||
                "N/A",
            valueSetter: (params) => {
                params.row.InvoiceNumber = params.value;
                return { ...params.row };
            },
        },
        {
            field: "InvoiceTotal",
            headerName: "Invoice Total",
            flex: 1,
            editable: true,
            valueGetter: (params) => {
                let val =
                    params.row.InvoiceTotal?.value || params.row.InvoiceTotal || 0;
                return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(val);
            },
            valueSetter: (params) => {
                params.row.InvoiceTotal = params.value;
                return { ...params.row };
            },
        },
        {
            field: "AmountDue",
            headerName: "Amount Due",
            flex: 1,
            editable: true,
            valueGetter: (params) => {
                let val = params.row.AmountDue?.value || params.row.AmountDue || 0;
                return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(val);
            },
            valueSetter: (params) => {
                params.row.AmountDue = params.value;
                return { ...params.row };
            },
        },
        {
            field: "viewImage",
            headerName: "View Image",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            if (params.row.scannedImage) {
                                setSelectedImage(params.row.scannedImage);
                                setSelectedFileType(params.row.scannedFileType || "image");
                                setImageModalOpen(true);
                            } else {
                                alert("No image available for this invoice.");
                            }
                        }}
                    >
                        View
                    </Button>
                );
            },
        },
    ];

    // ----------------------------------------
    // 7) RENDER
    // ----------------------------------------
    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: "#FFFFFF",
                color: "#000000",
                minHeight: "100vh",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0px 0px 30px 5px rgba(0,0,0,0.1)",
            }}
        >
            <Header
                title="FIG Agent Invoice Bulk Review"
                subtitle="This view allows K12 Nutrition Departments to organize their invoices and download a CSV file for further analysis. Upload invoice images to process them�the scanned data populates the table automatically and is exported to the database in the background."
                sx={{ color: "#000000" }}
                titleColor="#000000"
            />

            {/* UPLOAD SECTION */}
            <DropZone onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
                Drag and drop invoice images here or click below to browse
            </DropZone>
            <Box display="flex" flexDirection="column" gap={2} mb={2}>
                <Typography variant="h6" sx={{ color: "#000000" }}>
                    Upload Invoice Image(s)
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <input
                        type="file"
                        multiple
                        accept="image/png, image/jpeg, application/pdf"
                        onChange={handleFileSelect}
                        style={{ color: "#000000" }}
                    />
                    <Button
                        onClick={handleUploadAll}
                        variant="contained"
                        sx={{
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontWeight: "bold",
                            "&:hover": { backgroundColor: "#45A049" },
                        }}
                    >
                        Process Invoices
                    </Button>
                </Box>
            </Box>

            {/* THUMBNAILS */}
            {files.length > 0 && (
                <Box
                    display="flex"
                    flexDirection="row"
                    gap={2}
                    mb={2}
                    sx={{
                        overflowX: "auto",
                        padding: "8px",
                        borderBottom: "1px solid #4CAF50",
                    }}
                >
                    {files.map((file, idx) => {
                        const fileURL = URL.createObjectURL(file);
                        return <Thumbnail key={idx} src={fileURL} />;
                    })}
                </Box>
            )}

            {loading && <CircularProgress />}
            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}

            {/* TIP FOR ROW EDITING */}
            {invoiceData.length > 0 && (
                <Typography variant="caption" sx={{ color: "#000000", mb: 1 }}>
                    Tip: Double-click a row to edit. Press Enter to update the data in the database.
                </Typography>
            )}

            {/* INVOICE DATA TABLE */}
            <Box
                mt={4}
                sx={{
                    height: "auto",
                    "& .MuiDataGrid-root": {
                        border: "none",
                        backgroundColor: "#F5F5F5",
                        color: "#000000",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #4CAF50",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#E0E0E0",
                        color: "#000000",
                        borderBottom: "2px solid #4CAF50",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                        backgroundColor: "#FFFFFF",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                        backgroundColor: "#F5F5F5",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: "#E0E0E0",
                        color: "#000000",
                    },
                    "& .MuiCheckbox-root": {
                        color: "#4CAF50",
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: "#000000",
                    },
                }}
            >
                <DataGrid
                    rows={invoiceData}
                    columns={columns}
                    components={{ Toolbar: GridToolbar }}
                    getRowId={(row) =>
                        row.apiid ||
                        row.InvoiceId?.value ||
                        row.ID ||
                        row.InvoiceNumber ||
                        Math.random()
                    }
                    disableSelectionOnClick
                    pageSize={25}
                    rowsPerPageOptions={[25]}
                    density="comfortable"
                    autoHeight
                    processRowUpdate={handleProcessRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    // Collapsible detail panel for Items
                    getDetailPanelContent={({ row }) => (
                        <Box sx={{ padding: 2, backgroundColor: "#E0E0E0" }}>
                            {row.Items && row.Items.length > 0 ? (
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Description
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Quantity
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Unit
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Unit Price
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Product Code
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Date
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Tax
                                            </th>
                                            <th
                                                style={{
                                                    color: "#000000",
                                                    border: "1px solid #4CAF50",
                                                    padding: "4px",
                                                }}
                                            >
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {row.Items.map((item, index) => (
                                            <tr key={index}>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.Description?.value || ""}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.Quantity?.value || 0}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.Unit?.value || ""}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.UnitPrice?.value || 0}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.ProductCode?.value || ""}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.Date?.value || ""}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.Tax?.value || ""}
                                                </td>
                                                <td
                                                    style={{
                                                        color: "#000000",
                                                        border: "1px solid #4CAF50",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    {item.Amount?.value || 0}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <Typography sx={{ color: "#000000" }}>
                                    No items available
                                </Typography>
                            )}
                        </Box>
                    )}
                    getDetailPanelHeight={() => "auto"}
                />
            </Box>

            {/* DOWNLOAD BUTTONS */}
            {invoiceData.length > 0 && (
                <Box mt={2} display="flex" gap={2}>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontWeight: "bold",
                            "&:hover": { backgroundColor: "#45A049" },
                        }}
                        onClick={handleDownloadInvoiceDetailed}
                    >
                        Download Invoice Detailed
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontWeight: "bold",
                            "&:hover": { backgroundColor: "#45A049" },
                        }}
                        onClick={handleDownloadInvoiceSummary}
                    >
                        Download Invoice Summary
                    </Button>
                </Box>
            )}

            {/* CHAT WINDOW */}
            <Box
                mt={4}
                sx={{
                    backgroundColor: "#E0E0E0",
                    padding: "16px",
                    borderRadius: "8px",
                }}
            >
                <Typography variant="h6" sx={{ color: "#000000", mb: 2 }}>
                    FIG Agent Chat
                </Typography>
                <Box
                    sx={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        backgroundColor: "#FFFFFF",
                        padding: "8px",
                        borderRadius: "4px",
                        marginBottom: "8px",
                    }}
                >
                    {chatMessages.map((msg, index) => (
                        <Box key={index} sx={{ marginBottom: "4px" }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color:
                                        msg.sender === "user"
                                            ? "#4CAF50"
                                            : msg.sender === "agent"
                                                ? "#FF5722"
                                                : "#000000",
                                }}
                            >
                                {msg.sender === "user"
                                    ? "You"
                                    : msg.sender === "agent"
                                        ? "FIG Agent"
                                        : "System"}
                                : {msg.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Box display="flex" gap={2}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Type your message..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSendChat();
                                e.preventDefault();
                            }
                        }}
                        InputProps={{
                            sx: {
                                color: "#000000",
                                backgroundColor: "#FFFFFF",
                                borderRadius: "4px",
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSendChat}
                        sx={{
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontWeight: "bold",
                            "&:hover": { backgroundColor: "#45A049" },
                        }}
                    >
                        Send
                    </Button>
                </Box>
            </Box>

            {/* LOADING MODAL */}
            <Modal
                open={loading}
                hideBackdrop
                aria-labelledby="processing-modal-title"
                aria-describedby="processing-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 300,
                        bgcolor: "#FFFFFF",
                        border: "2px solid #4CAF50",
                        boxShadow: 24,
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        id="processing-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ color: "#000000" }}
                    >
                        Processing... {progress} of {files.length}
                    </Typography>
                    <Box mt={2}>
                        <CircularProgress sx={{ color: "#4CAF50" }} />
                    </Box>
                </Box>
            </Modal>

            {/* IMAGE MODAL */}
            <Modal
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                aria-labelledby="image-modal-title"
                aria-describedby="image-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "#FFFFFF",
                        boxShadow: 24,
                        p: 2,
                        borderRadius: "8px",
                        maxWidth: "90%",
                        maxHeight: "90%",
                        overflow: "auto",
                    }}
                >
                    <FloatingDocumentEditor imageSrc={selectedImage} fileType={selectedFileType} />
                </Box>
            </Modal>
        </Box>
    );
};

export default FIGAgentInvoiceBulkReview;
